import { QuizType } from '@repo/types/enums/quizType';
import { infer as Infer, z } from 'zod';

import { gradeMappingSchema } from '#grade-scale/gradeScale.schema';
import { groupPublicSchema } from '#group/groupPublic.schema';
import { userPublicSchema } from '#user/userPublic.schema';

import { quizBuilderQuestionSchema } from './quizBuilderQuestion.schema';
import { quizSectionSchema } from './quizSection.schema';

//basic quiz info schema
export const quizBuilderBasicInfoSchema = z.object({
  name: z.string().trim().min(1, 'Nazwa jest wymagana').max(128, 'Nazwa nie może przekraczać 128 znaków'),
  desc: z.string().trim().max(256, 'Opis nie może przekraczać 256 znaków').nullable(),
});

//quiz options schema
export const quizBuilderOptionsSchema = z
  .object({
    maxAttempts: z.number('Maksymalna liczba prób jest wymagana').min(0, 'Maksymalna liczba prób musi być większa lub równa 0').max(50, 'Maksymalna liczba prób nie może przekraczać 50'),
    time: z.number('Wpisz czas quizu').min(0, 'Czas musi być liczbą nieujemną'),
    deadline: z.iso.datetime('Nie prawidłowy').nullish(),
    available: z.iso.datetime('Nie prawidłowy').nullish(),
    freeNavigation: z.boolean(),
    instantMark: z.boolean(),
    gradeMapping: gradeMappingSchema.nullable(),
  })
  .refine(
    (data) => {
      if (!data.available || !data.deadline) return true;
      return new Date(data.deadline).getTime() > new Date(data.available).getTime();
    },
    {
      message: 'Termin musi być później niż data dostępności',
      path: ['deadline'],
    }
  );

//schema of section
export const quizBuilderSectionSchema = quizSectionSchema.extend({
  questions: z.array(quizBuilderQuestionSchema).min(1, 'Musisz dodać co najmniej 1 pytanie').max(1024, 'Możesz dodać maksymalnie 1024 pytania'),
});

export const quizBuilderUsersSchema = z.object({
  users: z.array(userPublicSchema),
  groups: z.array(groupPublicSchema),
});

export const quizBuilderSectionsSchema = z.object({
  content: z.array(quizBuilderSectionSchema).min(1, 'Musisz dodać co najmniej 1 sekcję'),
});

//whole builder schema
export const quizBuilderSchema = z.object({
  ...quizBuilderBasicInfoSchema.shape,
  ...quizBuilderOptionsSchema.shape,
  ...quizBuilderSectionsSchema.shape,
  force: z.boolean().optional(),
});

//create payload — POST /api/quiz requires a non-null `type`
export const quizCreateSchema = quizBuilderSchema.extend({
  type: z.enum(QuizType),
});

export const quizBuilderSchemaParser = quizBuilderSchema.transform((data) => {
  return {
    ...data,
    deadline: data.deadline || null,
    available: data.available || null,
  };
});

export type QuizBuilderBasicInfoSchema = Infer<typeof quizBuilderBasicInfoSchema>;
export type QuizBuilderOptionsSchema = Infer<typeof quizBuilderOptionsSchema>;
export type QuizBuilderSectionSchema = Infer<typeof quizBuilderSectionSchema>;
export type QuizBuilderSectionsSchema = Infer<typeof quizBuilderSectionsSchema>;
export type QuizBuilderUsersSchema = Infer<typeof quizBuilderUsersSchema>;
export type QuizBuilderSchema = Infer<typeof quizBuilderSchema>;
export type QuizCreateSchema = Infer<typeof quizCreateSchema>;
